/**
 * /ads — Ad control terminal. Visible ONLY to the Shieldz account
 * (is_platform_admin); the backend hard-gates every /api/admin/ads* call too.
 *
 * This is a revenue surface, so it's a data-forward operations console — not a
 * settings page: a KPI strip (est. revenue, impressions, clicks, CTR), a dense
 * filterable/sortable inventory table with inline status toggles, and a
 * slide-in inspector (with live preview) for editing — no centered dialogs.
 *
 * Each ad is a structured creative (banner + click URL + title) with an
 * optional raw-HTML override, a CPM rate (revenue per 1000 impressions), and a
 * placement (checkout rail / confirmation screen / both).
 */

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  API_BASE,
  createAd,
  deleteAd,
  listAds,
  updateAd,
  uploadAdImage,
  type Ad,
  type AdInput,
  type AdPlacement,
} from "../lib/api";
import { Banner, Btn, Toggle } from "../components/ui";
import { Ic } from "../components/Icons";

const resolveUrl = (u: string): string =>
  u.startsWith("/") ? `${API_BASE.replace(/\/+$/, "")}${u}` : u;

const PLACEMENT_LABEL: Record<AdPlacement, string> = {
  checkout: "Checkout rail",
  confirming: "Confirmation",
  both: "Both",
};

const usd = (cents: number): string =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number): string => n.toLocaleString();
const ctrOf = (a: Ad): number => (a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0);

type SortKey = "revenue" | "impressions" | "clicks" | "ctr" | "weight" | "newest";
type Filter = "all" | "active" | "paused" | AdPlacement;

export function Ads() {
  const [ads, setAds] = useState<Ad[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inspect, setInspect] = useState<Ad | "new" | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<SortKey>("revenue");

  const refetch = async () => {
    try {
      const r = await listAds();
      setAds(r.data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  useEffect(() => { void refetch(); }, []);

  const totals = useMemo(() => {
    const list = ads ?? [];
    const impressions = list.reduce((s, a) => s + a.impressions, 0);
    const clicks = list.reduce((s, a) => s + a.clicks, 0);
    return {
      revenue: list.reduce((s, a) => s + a.est_revenue_cents, 0),
      impressions,
      clicks,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      active: list.filter((a) => a.enabled).length,
      total: list.length,
    };
  }, [ads]);

  const view = useMemo(() => {
    let list = [...(ads ?? [])];
    if (filter === "active") list = list.filter((a) => a.enabled);
    else if (filter === "paused") list = list.filter((a) => !a.enabled);
    else if (filter === "checkout" || filter === "confirming" || filter === "both")
      list = list.filter((a) => a.placement === filter);
    const by: Record<SortKey, (a: Ad, b: Ad) => number> = {
      revenue: (a, b) => b.est_revenue_cents - a.est_revenue_cents,
      impressions: (a, b) => b.impressions - a.impressions,
      clicks: (a, b) => b.clicks - a.clicks,
      ctr: (a, b) => ctrOf(b) - ctrOf(a),
      weight: (a, b) => b.weight - a.weight,
      newest: (a, b) => b.created_at - a.created_at,
    };
    return list.sort(by[sort]);
  }, [ads, filter, sort]);

  if (error) {
    return <div className="scroll"><div className="pad"><Banner tone="fail">{error}</Banner></div></div>;
  }
  if (ads == null) {
    return <div className="scroll"><div className="pad" style={{ color: "var(--ink-3)" }}>Loading terminal…</div></div>;
  }

  return (
    <div className="scroll">
      <div className="pad fade-in" style={{ maxWidth: 1040 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>Ad terminal</h2>
            <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "4px 0 0" }}>
              Sponsored inventory on free-tier checkouts. Pro stays ad-free.
            </p>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <Btn variant="secondary" onClick={() => void refetch()}><Ic name="refund" size={14} /> Refresh</Btn>
            <Btn onClick={() => setInspect("new")}><Ic name="plus" size={14} /> New ad</Btn>
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Kpi label="Est. revenue" value={usd(totals.revenue)} accent />
          <Kpi label="Impressions" value={num(totals.impressions)} />
          <Kpi label="Clicks" value={num(totals.clicks)} />
          <Kpi label="CTR" value={`${totals.ctr.toFixed(2)}%`} />
          <Kpi label="Active" value={`${totals.active}/${totals.total}`} />
        </div>

        {/* Toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
          <div className="set-seg" style={{ flexWrap: "wrap" }}>
            {(["all", "active", "paused", "checkout", "confirming", "both"] as Filter[]).map((f) => (
              <button key={f} type="button" className={`set-seg-opt${filter === f ? " on" : ""}`} onClick={() => setFilter(f)}>
                {f === "all" ? "All" : f === "active" ? "Active" : f === "paused" ? "Paused" : PLACEMENT_LABEL[f]}
              </button>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-3)" }}>
            Sort
            <select className="input" style={{ width: "auto", padding: "6px 10px" }} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="revenue">Revenue</option>
              <option value="impressions">Impressions</option>
              <option value="clicks">Clicks</option>
              <option value="ctr">CTR</option>
              <option value="weight">Weight</option>
              <option value="newest">Newest</option>
            </select>
          </label>
        </div>

        {/* Inventory table */}
        {view.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>
            <div style={{ fontSize: 14, marginBottom: 4 }}>{ads.length === 0 ? "No ads yet." : "No ads match this filter."}</div>
            {ads.length === 0 && <div style={{ fontSize: 12.5 }}>Create one to start showing sponsored units on free-tier checkouts.</div>}
          </div>
        ) : (
          <div className="card" style={{ overflow: "hidden", padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--ink-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  <th style={th}>Ad</th>
                  <th style={th}>Placement</th>
                  <th style={thR}>CPM</th>
                  <th style={thR}>Impr.</th>
                  <th style={thR}>Clicks</th>
                  <th style={thR}>CTR</th>
                  <th style={thR}>Revenue</th>
                  <th style={{ ...thR, width: 64 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {view.map((a) => (
                  <AdRow key={a.id} ad={a} onOpen={() => setInspect(a)} onChange={refetch} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inspect && (
        <Inspector
          ad={inspect === "new" ? undefined : inspect}
          onClose={() => setInspect(null)}
          onSaved={() => { setInspect(null); void refetch(); }}
          onDeleted={() => { setInspect(null); void refetch(); }}
        />
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: "11px 14px", fontWeight: 600, borderBottom: "1px solid var(--line-2)" };
const thR: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "11px 14px", borderBottom: "1px solid var(--line)", verticalAlign: "middle" };
const tdR: React.CSSProperties = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontFamily: "var(--mono, monospace)" };

function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 650, marginTop: 4, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", color: accent ? "var(--brand-700, #1f7a4d)" : "var(--ink)" }}>{value}</div>
    </div>
  );
}

function AdRow({ ad, onOpen, onChange }: { ad: Ad; onOpen: () => void; onChange: () => Promise<void> | void }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      await updateAd(ad.id, {
        title: ad.title, target_url: ad.target_url, html: ad.html,
        placement: ad.placement, weight: ad.weight, enabled: !ad.enabled, cpm_usd_cents: ad.cpm_usd_cents,
      });
      await onChange();
    } finally { setBusy(false); }
  };
  return (
    <tr style={{ cursor: "pointer", opacity: ad.enabled ? 1 : 0.5 }} onClick={onOpen}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
      <td style={td}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 48, height: 32, borderRadius: 6, flex: "none", overflow: "hidden", background: "var(--surface-2)", display: "grid", placeItems: "center", border: "1px solid var(--line-2)" }}>
            {ad.image_url ? <img src={resolveUrl(ad.image_url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : ad.html ? <span style={{ fontSize: 9, color: "var(--ink-3)", fontWeight: 700 }}>HTML</span>
              : <Ic name="share" size={13} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{ad.title || <span style={{ color: "var(--ink-3)" }}>Untitled</span>}</div>
            {ad.target_url && <div style={{ fontSize: 11, color: "var(--ink-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{ad.target_url.replace(/^https?:\/\//, "")}</div>}
          </div>
        </div>
      </td>
      <td style={td}>{PLACEMENT_LABEL[ad.placement]}</td>
      <td style={tdR}>{ad.cpm_usd_cents > 0 ? usd(ad.cpm_usd_cents) : "—"}</td>
      <td style={tdR}>{num(ad.impressions)}</td>
      <td style={tdR}>{num(ad.clicks)}</td>
      <td style={tdR}>{ctrOf(ad).toFixed(1)}%</td>
      <td style={{ ...tdR, fontWeight: 600 }}>{ad.est_revenue_cents > 0 ? usd(ad.est_revenue_cents) : "—"}</td>
      <td style={{ ...tdR }} onClick={(e) => e.stopPropagation()}>
        <Toggle on={ad.enabled} onChange={() => { if (!busy) void toggle(); }} ariaLabel={`Toggle ${ad.title || ad.id}`} />
      </td>
    </tr>
  );
}

function Inspector({ ad, onClose, onSaved, onDeleted }: { ad?: Ad; onClose: () => void; onSaved: () => void; onDeleted: () => void }) {
  const [title, setTitle] = useState(ad?.title ?? "");
  const [placement, setPlacement] = useState<AdPlacement>(ad?.placement ?? "both");
  const [targetUrl, setTargetUrl] = useState(ad?.target_url ?? "");
  const [weight, setWeight] = useState(String(ad?.weight ?? 1));
  const [cpm, setCpm] = useState(ad ? (ad.cpm_usd_cents / 100).toString() : "");
  const [enabled, setEnabled] = useState(ad?.enabled ?? true);
  const [showHtml, setShowHtml] = useState(!!ad?.html);
  const [html, setHtml] = useState(ad?.html ?? "");
  const [imageUrl, setImageUrl] = useState<string | null>(ad?.image_url ?? null);
  const [adId, setAdId] = useState<number | null>(ad?.id ?? null);
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = (): AdInput => ({
    title: title.trim(),
    placement,
    target_url: targetUrl.trim() || null,
    html: showHtml ? html.trim() || null : null,
    weight: Math.max(1, Math.min(1000, Number.parseInt(weight, 10) || 1)),
    cpm_usd_cents: Math.max(0, Math.round((Number.parseFloat(cpm) || 0) * 100)),
    enabled,
  });

  const ensureId = async (): Promise<number | null> => {
    if (adId != null) return adId;
    const created = await createAd(body());
    setAdId(created.id);
    return created.id;
  };

  const onPickImage = async (file: File | null) => {
    if (!file) return;
    setError(null); setImgBusy(true);
    try {
      const id = await ensureId();
      if (id == null) return;
      const r = await uploadAdImage(id, file);
      setImageUrl(r.image_url);
    } catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setImgBusy(false); }
  };

  const save = async () => {
    setError(null); setBusy(true);
    try {
      if (adId != null) await updateAd(adId, body());
      else await createAd(body());
      onSaved();
    } catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (adId == null) { onClose(); return; }
    if (!window.confirm(`Delete "${title || adId}"? This can't be undone.`)) return;
    setBusy(true);
    try { await deleteAd(adId); onDeleted(); }
    finally { setBusy(false); }
  };

  // Live preview of the rendered creative.
  const preview = showHtml && html.trim()
    ? <div dangerouslySetInnerHTML={{ __html: html }} />
    : imageUrl
      ? <img src={resolveUrl(imageUrl)} alt="" style={{ width: "100%", borderRadius: 10, display: "block" }} />
      : <div style={{ padding: "14px 16px", fontSize: 13.5, color: "var(--ink-2)" }}>{title || "Your ad"}</div>;

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(12,26,36,.32)", display: "flex", justifyContent: "flex-end" }}>
      <div style={{ width: "min(460px, 100vw)", height: "100%", background: "var(--surface)", boxShadow: "-12px 0 40px -12px rgba(0,0,0,.3)", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: "1px solid var(--line-2)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{ad ? "Edit ad" : "New ad"}</h3>
          <button className="sheet-x" onClick={onClose} aria-label="Close"><Ic name="x" size={16} /></button>
        </div>

        <div className="scroll" style={{ flex: 1, padding: 18 }}>
          {error && <div style={{ marginBottom: 14 }}><Banner tone="fail">{error}</Banner></div>}

          {/* Live preview */}
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Preview</div>
          <div style={{ border: "1px solid var(--line-2)", borderRadius: 12, overflow: "hidden", marginBottom: 18, background: "var(--surface-2)" }}>{preview}</div>

          {/* Performance (edit mode) */}
          {ad && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 18 }}>
              <MiniStat label="Impr." value={num(ad.impressions)} />
              <MiniStat label="Clicks" value={num(ad.clicks)} />
              <MiniStat label="Revenue" value={ad.est_revenue_cents > 0 ? usd(ad.est_revenue_cents) : "—"} />
            </div>
          )}

          <div className="field">
            <label>Banner image</label>
            <label className="btn btn-secondary" style={{ cursor: "pointer", marginTop: 6, display: "inline-flex" }}>
              <Ic name={imageUrl ? "refund" : "plus"} size={14} /> {imgBusy ? "Uploading…" : imageUrl ? "Replace image" : "Upload image (≤4 MB)"}
              <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)} />
            </label>
            <div className="help" style={{ marginTop: 6 }}>PNG/JPEG/WebP. A new ad is saved as a draft when you upload.</div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="i-title">Title</label>
            <input id="i-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Trade fee-free on Acme DEX" />
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="i-url">Click-through URL</label>
            <input id="i-url" className="input" type="url" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://…" />
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>Placement</label>
            <div className="set-seg" style={{ marginTop: 4 }}>
              {(["checkout", "confirming", "both"] as AdPlacement[]).map((p) => (
                <button key={p} type="button" className={`set-seg-opt${placement === p ? " on" : ""}`} onClick={() => setPlacement(p)}>
                  {p === "both" ? "Both" : p === "checkout" ? "Checkout" : "Confirming"}
                </button>
              ))}
            </div>
          </div>

          <div className="row" style={{ marginTop: 14, gap: 12, flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, minWidth: 110 }}>
              <label htmlFor="i-cpm">CPM (USD)</label>
              <input id="i-cpm" className="input mono" type="text" inputMode="decimal" value={cpm} onChange={(e) => setCpm(e.target.value)} placeholder="0.00" />
              <div className="help">Revenue / 1000 views.</div>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 110 }}>
              <label htmlFor="i-weight">Weight</label>
              <input id="i-weight" className="input mono" type="text" inputMode="numeric" value={weight} onChange={(e) => setWeight(e.target.value)} />
              <div className="help">Higher = shown first.</div>
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Enabled</span>
              <Toggle on={enabled} onChange={setEnabled} ariaLabel="Enabled" />
            </label>
          </div>

          <div style={{ marginTop: 14 }}>
            {!showHtml ? (
              <button type="button" className="btn btn-ghost" onClick={() => setShowHtml(true)}>
                <Ic name="code" size={14} /> Advanced: raw HTML embed
              </button>
            ) : (
              <div className="field">
                <label htmlFor="i-html">Raw HTML (advanced)</label>
                <textarea id="i-html" className="input" rows={4} spellCheck={false} value={html} onChange={(e) => setHtml(e.target.value)} placeholder="<a href=…><img …></a>" style={{ resize: "vertical", fontFamily: "var(--mono, monospace)" }} />
                <div className="help" style={{ color: "var(--warn-ink, #9a6700)" }}>Renders verbatim on the payment page — only paste code you trust. Overrides image/title.</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, padding: "14px 18px", borderTop: "1px solid var(--line-2)" }}>
          <Btn block onClick={() => void save()} loading={busy}>{adId != null ? "Save" : "Create"}</Btn>
          {adId != null && <Btn variant="ghost" onClick={() => void remove()} loading={busy}>Delete</Btn>}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "var(--surface-2)", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 10.5, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 650, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
